import { useEffect, useState } from "react"
import SidePanel from "~/app/components/SidePanel"
import classNames from "classnames"
import { gql } from "@apollo/client"
import { useQuery } from "@oysterhr/core"
import { formattedDateString } from "@oysterhr/common"
import { Icon, LoadingSpinner } from "@oysterhr/ui"

export const AUDIT_HISTORY = gql`
  query AuditHistory($objectId: ID!, $only: [String!]) {
    auditableObject(id: $objectId) {
      auditHistory(only: $only) {
        by {
          id
          name
        }
        when
        changes {
          humanFieldName
          from
          to
        }
        event
      }
    }
  }
`
// We use network-only here because we want to always refetch the history log from server
export const useAuditLog = (objectId: string, only: string[] = []) =>
  useQuery(AUDIT_HISTORY, { variables: { objectId, only }, fetchPolicy: "network-only" })

const dateTimeWIthZone = (dateString: string) =>
  formattedDateString(dateString, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
    timeZone: "UTC",
    timeZoneName: "short",
  })

interface AuditQueryRowProps {
  by: {
    id: string
    name: string
  }
  when: string
  changes: Array<{
    humanFieldName: string
    from: string
    to: string
  }>
  to: string
  event: string
}

const AuditQueryRow = ({ by, when, changes, event }: AuditQueryRowProps, i: number) => (
  <div
    className={classNames("p-4 text-sm", {
      "bg-primary-lighten-100": i % 2 === 0,
    })}
    key={i}
  >
    <span className="font-bold">{`${dateTimeWIthZone(when)} - `}</span>
    <span className="font-semibold">{by.name}</span> {`${event}d`}:
    <ol>
      {changes.map(({ humanFieldName, from, to }) => (
        <li key={humanFieldName}>
          <span>- </span>
          <span className="font-semibold">{humanFieldName}</span>

          {event === "create" ? (
            <span className="font-semibold">: {to}</span>
          ) : (
            <>
              {" "}
              from <span className="font-semibold">{from || "null"}</span> to{" "}
              <span className="font-semibold">{to || "null"}</span>
            </>
          )}
        </li>
      ))}
    </ol>
  </div>
)

interface AuditQueryProps {
  objectId: string
  only?: Array<any>
}

const AuditQuery = ({ objectId, only }: AuditQueryProps) => {
  const { data, loading } = useAuditLog(objectId, only)

  const auditHistory = data?.auditableObject.auditHistory || []

  if (loading) return <LoadingSpinner size="md" />
  if (!auditHistory.length) return <span>There are no history changes for this item</span>

  return <>{auditHistory.map(AuditQueryRow)}</>
}

type AuditDrawerTriggerProps = {
  onClick: () => void
  label?: string
}

const AuditDrawerTrigger = ({ onClick, label }: AuditDrawerTriggerProps) => (
  <button className="cursor-pointer select-none" data-testid="audit-history-icon" onClick={onClick}>
    {label && <span className="mr-2">{label}</span>}

    <Icon name="ClockCounterClockwise" className="text-primary-base" />
  </button>
)

interface AuditDrawerProps {
  objectId: string
  only?: Array<any>
  label?: string
  isOpen?: boolean
  showTrigger?: boolean
  onClose?: () => void
}
const AuditDrawer = ({
  objectId,
  only,
  label,
  isOpen = false,
  showTrigger = true,
  onClose,
}: AuditDrawerProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState<boolean>()

  useEffect(() => {
    setInternalIsOpen(isOpen)
  }, [isOpen])

  return (
    <>
      <SidePanel
        wide
        title="Changes History"
        open={internalIsOpen}
        onRequestClose={() => {
          setInternalIsOpen(false)
          onClose?.()
        }}
      >
        <div className="h-full w-full overflow-y-auto">
          {internalIsOpen && <AuditQuery objectId={objectId} only={only} />}
        </div>
      </SidePanel>
      {showTrigger && (
        <AuditDrawerTrigger onClick={() => setInternalIsOpen(!internalIsOpen)} label={label} />
      )}
    </>
  )
}

export default AuditDrawer
